import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell } from "lucide-react";

const TIPO_LABELS = {
  cobro_vencido: "Cobro vencido", duplicado_detectado: "Duplicado detectado",
  margen_excedido: "Margen excedido", cobro_registrado: "Cobro registrado",
  pago_registrado: "Pago registrado", load_desbloqueado: "Load desbloqueado",
  prueba: "Alerta de prueba", resumen_diario: "Resumen diario",
};
const SEVERIDAD_DOT = { info: "bg-slate-400", media: "bg-amber-500", alta: "bg-orange-500", critica: "bg-red-500" };

export function AlertBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [recent, setRecent] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts/unread-count");
      setCount(data.count);
    } catch { /* silencioso: la campanita no debe romper la app */ }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [poll]);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      const { data } = await api.get("/alerts/recent");
      setRecent(data);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
        onClick={toggleOpen}
        data-testid="alert-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white" data-testid="alert-bell-count">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 rounded-md border border-border bg-white shadow-lg" data-testid="alert-bell-dropdown">
          <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase text-muted-foreground">Alertas recientes</div>
          <div className="max-h-80 overflow-y-auto">
            {recent.length === 0 && <div className="p-4 text-center text-xs text-muted-foreground">Sin alertas.</div>}
            {recent.map((a, i) => (
              <div key={a.id} className={`border-b border-border/60 px-3 py-2 text-sm ${!a.leida ? "bg-green-50/50" : ""}`} data-testid={`alert-bell-item-${i}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${SEVERIDAD_DOT[a.severidad] || "bg-slate-400"}`} />
                  <span className="font-medium">{TIPO_LABELS[a.tipo] || a.tipo}</span>
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{a.mensaje}</p>
              </div>
            ))}
          </div>
          <button
            className="block w-full px-3 py-2 text-center text-xs font-semibold text-primary hover:bg-slate-50"
            onClick={() => { setOpen(false); navigate("/alerts"); }}
            data-testid="alert-bell-view-all"
          >
            Ver todas las alertas
          </button>
        </div>
      )}
    </div>
  );
}
